/**
 * Notes Store - Personal notes with markdown support
 * Hanterar användarens personliga anteckningar med markdown och taggning
 */

import { create } from 'zustand';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api-client';
import { useFamilyStore } from './family-store';
import { logActivity } from '@/lib/activity-logger';

export interface Note {
  id: string;
  userId: string;
  title: string;
  content: string; // Markdown
  tags: string[];
  isPinned: boolean;
  isPrivate: boolean; // Private notes are always saved to user's own profile
  authorId: string;
  authorName: string;
  lastModifiedBy?: string;
  lastModifiedById?: string;
  createdAt: string;
  updatedAt: string;
}

interface NotesState {
  notes: Note[];
  totalCount: number;
  
  // Actions
  fetchFromServer: () => Promise<void>;
  addNote: (note: Omit<Note, 'id' | 'userId' | 'authorId' | 'authorName' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateNote: (noteId: string, updates: Partial<Pick<Note, 'title' | 'content' | 'tags' | 'isPinned'>>) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
  togglePin: (noteId: string) => Promise<void>;
  
  // Selectors
  getNoteById: (noteId: string) => Note | undefined;
  getNotesByTag: (tag: string) => Note[];
  searchNotes: (query: string) => Note[];
  getAllTags: () => string[];
}

export const useNotesStore = create<NotesState>()((set, get) => ({
  notes: [],
  totalCount: 0,

  fetchFromServer: async () => {
    try {
      const familyId = useFamilyStore.getState().family?.id;
      const url = familyId ? `/notes?familyId=${familyId}` : '/notes';
      console.log('[NotesStore] Fetching notes from:', url);
      const res = await apiGet<{ notes: Note[]; totalCount: number }>(url);
      console.log('[NotesStore] Response:', res);
      if (res.success && res.data) {
        console.log('[NotesStore] Setting notes:', res.data.notes.length, 'notes');
        set({ notes: res.data.notes, totalCount: res.data.totalCount });
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  },

      addNote: async (note) => {
        try {
          const familyId = useFamilyStore.getState().family?.id;
          const url = familyId ? `/notes?familyId=${familyId}` : '/notes';
          const res = await apiPost<Note>(url, note);
          if (res.success && res.data) {
            logActivity({ action: 'CREATE_NOTE', category: 'notes', description: 'Note created', metadata: { noteId: res.data.id, title: note.title } });
            set((state) => ({ 
              notes: [res.data as Note, ...state.notes],
              totalCount: state.totalCount + 1,
            }));
          }
        } catch (error) {
          console.error('Failed to add note:', error);
          throw error;
        }
      },

      updateNote: async (noteId, updates) => {
        try {
          const familyId = useFamilyStore.getState().family?.id;
          const url = familyId ? `/notes/${noteId}?familyId=${familyId}` : `/notes/${noteId}`;
          const res = await apiPut<Note>(url, updates);
          if (res.success && res.data) {
            logActivity({ action: 'UPDATE_NOTE', category: 'notes', description: 'Note updated', metadata: { noteId } });
            set((state) => ({
              notes: state.notes.map((n) => (n.id === noteId ? res.data as Note : n)),
            }));
          }
        } catch (error) {
          console.error('Failed to update note:', error);
          throw error;
        }
      },

      deleteNote: async (noteId) => {
        try {
          const familyId = useFamilyStore.getState().family?.id;
          const url = familyId ? `/notes/${noteId}?familyId=${familyId}` : `/notes/${noteId}`;
          const res = await apiDelete(url);
          if (res.success) {
            logActivity({ action: 'DELETE_NOTE', category: 'notes', description: 'Note deleted', metadata: { noteId } });
            set((state) => ({ 
              notes: state.notes.filter((n) => n.id !== noteId),
              totalCount: Math.max(0, state.totalCount - 1),
            }));
          }
        } catch (error) {
          console.error('Failed to delete note:', error);
          throw error;
        }
      },

      togglePin: async (noteId) => {
        try {
          const note = get().notes.find((n) => n.id === noteId);
          if (!note) return;

          const familyId = useFamilyStore.getState().family?.id;
          const url = familyId ? `/notes/${noteId}?familyId=${familyId}` : `/notes/${noteId}`;
          
          const res = await apiPut(url, { isPinned: !note.isPinned });
          if (res.success && res.data) {
            set((state) => ({
              notes: state.notes.map((n) => (n.id === noteId ? res.data as Note : n)),
            }));
          }
        } catch (error) {
          console.error('Failed to toggle pin:', error);
          throw error;
        }
      },

      getNoteById: (noteId) => {
        return get().notes.find((n) => n.id === noteId);
      },

      getNotesByTag: (tag) => {
        return get().notes.filter((n) => n.tags.includes(tag));
      },

      searchNotes: (query) => {
        const lowerQuery = query.toLowerCase();
        return get().notes.filter(
          (n) =>
            n.title.toLowerCase().includes(lowerQuery) ||
            n.content.toLowerCase().includes(lowerQuery) ||
            n.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
        );
      },

      getAllTags: () => {
        const allTags = get().notes.flatMap((n) => n.tags);
        return Array.from(new Set(allTags)).sort();
      },
}));

// Hydration hook - always returns true since we don't persist
export function useNotesHydrated() {
  return true;
}
